import { CustomTooltip } from '@/components/custom-tooltip';
import { Button } from '@/components/ui/button';
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
} from '@/components/ui/dialog';
import { formatDateWithGivenFormatter } from 'features/dates/date-helpers';
import { selectProperty } from 'features/property/redux/selectors';
import { useUser } from 'features/users/context/userContext';
import { labels } from 'locales/en.label';
import {
	formattedCurrency,
	twoDecimalRateFormatter,
} from 'pages/MyView/tableCustomCellRender';
import { useState } from 'react';
import { DiJqueryLogo } from 'react-icons/di';
import { FiUpload } from 'react-icons/fi';
import { IoMdArrowDropdown, IoMdArrowDropup } from 'react-icons/io';
import { useAppSelector } from 'redux/hooks';
import styles from '../../../common/_variables.module.scss';
import { selectPendingRecs } from '../redux/rate-upload-selectors';

interface RateUploadButtonProps {
	/**
	 * Whether the button is disabled or not
	 */
	disabled: boolean;
	/**
	 * Function that triggers the rate upload process
	 * @returns a promise that resolves to the SNS message ID
	 */
	handleTriggerUpload: () => Promise<any>;
	/**
	 * Whether we are polling for Rec Rate updates after an upload
	 */
	polling: boolean;
	/**
	 * Marked as true while we are sending the request to SNS
	 */
	uploading: boolean;
	/**
	 * Optional className for the IconButton
	 */
	className?: string;
}

export const RateUploadButton = ({
	disabled,
	handleTriggerUpload,
	polling,
	uploading,
	className,
}: RateUploadButtonProps) => {
	const { currencyCode } = useAppSelector(selectProperty);
	const pendingRecs = useAppSelector(selectPendingRecs);
	const { locale } = useUser();
	const [isOpen, setIsOpen] = useState(false);

	const handleConfirm = () => {
		handleTriggerUpload();
		setIsOpen(false);
	};

	return (
		<>
			<Dialog open={isOpen} onOpenChange={setIsOpen}>
				<DialogContent className='tw-max-w-lg tw-p-6 tw-rounded-lg'>
					<DialogHeader className='tw-mb-4 tw-pb-2 tw-border-b tw-border-red-500'>
						<DialogTitle className='tw-text-xl tw-font-bold'>
							{labels.rate_upload.header}
						</DialogTitle>
						<DialogDescription className='tw-text-slate-600 tw-mt-2'>
							{labels.rate_upload.verify_msg}
						</DialogDescription>
					</DialogHeader>

					<div className='tw-border tw-rounded-md tw-overflow-hidden tw-bg-white'>
						<div className='tw-grid tw-grid-cols-4 tw-font-semibold tw-border-b'>
							<div className='tw-p-3 tw-text-left'>
								Stay
								<br />
								Date
							</div>
							<div className='tw-p-3 tw-text-left'>
								Rec
								<br />
								Rate
							</div>
							<div className='tw-p-3 tw-text-left'>
								BWH
								<br />
								Rate
							</div>
							<div className='tw-p-3 tw-text-left'>
								Overridden
								<br />
								Rate
							</div>
						</div>
						<div className='tw-max-h-[300px] tw-overflow-y-auto'>
							{pendingRecs?.map((rec) => {
								// The grid shows the live BWH rate when it differs from the
								// nightly snapshot, so the dialog has to use the same value or
								// it will ask users to confirm against a stale rate.
								const currentRate = rec.current_rate_live ?? rec.current_rate;
								return (
									<div
										key={rec.stay_date}
										className='tw-grid tw-grid-cols-4 tw-border-b'
									>
										<div className='tw-p-3 tw-text-left'>
											{formatDateWithGivenFormatter(rec.stay_date, 'M/DD/YY')}
										</div>
										<div className='tw-p-3 tw-text-left'>
											{formattedCurrency(
												rec.rec_rate?.toString() || '0',
												locale,
												currencyCode,
												true,
												twoDecimalRateFormatter,
											)}
										</div>
										<div className='tw-p-3 tw-text-left'>
											{formattedCurrency(
												currentRate?.toString() || '0',
												locale,
												currencyCode,
												true,
												twoDecimalRateFormatter,
											)}
										</div>
										<div className='tw-p-3 tw-text-left tw-flex tw-items-center tw-gap-2'>
											{formattedCurrency(
												rec.override_rate?.toString() || '0',
												locale,
												currencyCode,
												true,
												twoDecimalRateFormatter,
											)}
											{rec.override_rate != null && currentRate != null ? (
												rec.override_rate > currentRate ? (
													<IoMdArrowDropup
														size={20}
														color={styles.colorGreen}
													/>
												) : rec.override_rate < currentRate ? (
													<IoMdArrowDropdown
														size={20}
														color={styles.colorWarning}
													/>
												) : null
											) : null}
										</div>
									</div>
								);
							})}
						</div>
					</div>

					<div className='tw-flex tw-justify-end tw-gap-3 tw-mt-6'>
						<Button
							variant='outline'
							className='tw-min-w-[100px]'
							onClick={() => setIsOpen(false)}
						>
							{labels.common.cancel}
						</Button>
						<Button
							variant='outline'
							className='tw-min-w-[100px] tw-bg-green hover:tw-bg-greenHover tw-text-white'
							onClick={handleConfirm}
						>
							{labels.rate_upload.confirm_btn}
						</Button>
					</div>
				</DialogContent>
			</Dialog>
			<CustomTooltip content={<p>{labels.rate_upload.tooltip_title}</p>}>
				<span>
					<Button
						variant='ghost'
						size='icon'
						className={
							className
								? `tw-flex tw-items-center tw-justify-center tw-p-2 tw-bg-transparent tw-border-none tw-cursor-pointer tw-text-center tw-mx-1 ${className}`
								: 'tw-flex tw-items-center tw-justify-center tw-p-2 tw-bg-transparent tw-border-none tw-cursor-pointer tw-text-center tw-mx-1'
						}
						style={{
							width: '40px',
							height: '40px',
						}} /* Match MUI IconButton dimensions */
						onClick={() => {
							console.log('upload clicked');
							setIsOpen(true);
						}}
						disabled={disabled || polling || uploading}
					>
						{polling ? (
							<DiJqueryLogo
								size={22} /* Match the exact size of other icons */
								className='tw-animate-spin'
								style={{ color: '#905959' }}
							/>
						) : (
							<FiUpload
								size={22} /* Match the exact size of other icons */
								className={uploading ? 'tw-animate-spin' : ''}
								style={{ color: '#905959' }}
								stroke='currentColor'
								strokeWidth={2}
							/>
						)}
					</Button>
				</span>
			</CustomTooltip>
		</>
	);
};
